"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

const LevelUpContext = createContext();

/**
 * Durée d'affichage de la carte. Assez pour être lue, assez court pour ne pas
 * s'installer : la montée de niveau peut tomber en pleine vidéo de formation,
 * elle ne doit jamais prendre l'écran en otage.
 */
const VISIBLE_MS = 4500;

export function LevelUpProvider({ children }) {
    // { level, progress } le temps de l'affichage, null le reste du temps.
    const [levelUp, setLevelUp] = useState(null);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const dismissLevelUp = useCallback(() => {
        clearTimeout(timerRef.current);
        setLevelUp(null);
    }, []);

    // Stable : Level la garde dans les dépendances de son effet, une identité
    // qui change à chaque rendu du provider le relancerait pour rien.
    const triggerLevelUp = useCallback((level, progress = 0) => {
        clearTimeout(timerRef.current);
        setLevelUp({ level, progress });
        timerRef.current = setTimeout(() => setLevelUp(null), VISIBLE_MS);
    }, []);

    const value = useMemo(
        () => ({ levelUp, triggerLevelUp, dismissLevelUp }),
        [levelUp, triggerLevelUp, dismissLevelUp],
    );

    return (
        <LevelUpContext.Provider value={value}>
            {children}
        </LevelUpContext.Provider>
    );
}

export function useLevelUp() {
    const context = useContext(LevelUpContext);
    if (!context) {
        throw new Error("useLevelUp must be used within a LevelUpProvider");
    }
    return context;
}
